import { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';

import SectionCard from '@/components/common/SectionCard/SectionCard';
import DatePickerRange from '@/components/common/DatePickerRange/DatePickerRange';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import useDownloadUpliftTemplate, { DateRangeAndInterval } from './hooks/useDownloadUpliftTemplate';
import { initialValues, validationSchema } from './validationSchema';
import DownloadBillingInterval from './DownloadBillingInterval';
import { CheckboxChange } from '@/components/common/CheckboxTag4/CheckboxTag4';
import styles from './download-template.module.scss';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';

export default function DownloadTemplateContainer() {
  const [billingIntervalsSelected, setBillingIntervalsSelected] = useState<string[]>([]);
  const { downloadFile } = useDownloadUpliftTemplate();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  const fullAccessOrAdminOrUserRole =
    userRole == UserRole.FULL_ACCESS || userRole == UserRole.ADMIN || userRole == UserRole.USER;

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getRole();
  }, [getUserRole]);

  const onSubmitForm = async (values: DateRangeAndInterval, { resetForm, validateForm }:
  FormikHelpers<DateRangeAndInterval>) => {
    const intervalNamesJoint = billingIntervalsSelected.map(interval => interval.replace(/\s|\-/g, ''));
    const dateRangeAndInterval = {
      dateFrom: values.dateFrom,
      dateTo: values.dateTo,
      billingIntervals: intervalNamesJoint.length ? intervalNamesJoint : null,
    };

    const resetAndValidate = () => {
      resetForm();
      validateForm();
    };
    downloadFile(dateRangeAndInterval, resetAndValidate);
    setBillingIntervalsSelected([]);
  };

  const onBillingIntervalChange = (event: CheckboxChange) => {
    if (event.checked && event.name) {
      setBillingIntervalsSelected([...billingIntervalsSelected, event.name]);
    } else if (event.name) {
      setBillingIntervalsSelected([...billingIntervalsSelected.filter(interval => interval !== event.name)]);
    }
  };

  return (
    <SectionCard
      padding={true}
      title={{ titleText: 'Download Template' }}
    >
      <Formik<DateRangeAndInterval>
        initialValues={initialValues}
        onSubmit={onSubmitForm}
        validationSchema={validationSchema}
        validateOnMount={true}
      >
        {({ errors, setFieldValue, values, isValid, setFieldTouched, touched }) => (
          <Form>
            <div className="row">
              <div className="col-5">
                <label
                  className="d-block mb-10 fw-500"
                  htmlFor="dateFrom"
                >
                  Next Billing Date between: {<span className={styles.required}>*</span>}
                </label>

                <div className="mb-20">
                  <DatePickerRange
                    from={{
                      datepicker: {
                        id: 'dateFrom',
                        value: values.dateFrom,
                        placeholder: 'Start Date',
                        onChange: ({ value }) => {
                          setFieldValue('dateFrom', value);
                          setFieldTouched('dateFrom', true, false);
                        },
                      },
                    }}
                    to={{
                      datepicker: {
                        id: 'dateTo',
                        value: values.dateTo,
                        placeholder: 'End Date',
                        onChange: ({ value }) => {
                          setFieldValue('dateTo', value);
                          setFieldTouched('dateTo', true, false);
                        },
                      },
                    }}
                  />
                  {errors?.dateFrom && touched?.dateFrom && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                      {errors.dateFrom}
                    </div>
                  )}
                  {errors?.dateTo && touched?.dateTo && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                      {errors.dateTo}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.collapsible}>
                <DownloadBillingInterval billingIntervalsSelected={billingIntervalsSelected}
                  onBillingIntervalChange={onBillingIntervalChange} />
              </div>

            </div>
            <div className="d-flex"  >
              {fullAccessOrAdminOrUserRole && (
                <div className={styles.button}>
                  <span className="d-block mb-10 fw-500">Billing Cycles Template</span>
                  <Button
                    type='submit'
                    buttonStyleType={ButtonStyleType.PRIMARY}
                    disabled={!isValid || !(touched.dateFrom && touched.dateTo)}
                  > Download
                  </Button>
                </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </SectionCard>
  );
}
